import {
  Box,
  Chip,
  Tabs,
  Tooltip,
  TooltipProps,
  Typography,
  styled,
  tooltipClasses,
} from "@mui/material";
import { FormikValues, useFormikContext } from "formik";
import React from "react";
import { FilterFormProps, Query } from "./AdvancedFilter";
import { isEmpty } from "ramda";

interface FilterBarProps {
  sections?: FilterFormProps["sections"];
  isSubmitted?: boolean;
  formatFilterData: (values: FormikValues) => {
    [k: string]: unknown;
  };
  handleRemoveFilter: (fieldName: string) => void;
}

const classes = {
  tabs: {
    justifyContent: "center",
    alignItems: "center",
    minHeight: "unset",
  },
  tabIndicator: {
    backgroundColor: "unset",
  },
  chip: {
    marginLeft: 1,
  },
  tooltip: {
    fontSize: 12,
  },
  filterWrapper: {
    width: "100%",
    maxWidth: "700px",
  },
};

const FilterTooltip = styled(({ className, ...props }: TooltipProps) => (
  <Tooltip {...props} arrow classes={{ popper: className }} />
))(({ theme }) => ({
  [`& .${tooltipClasses.arrow}`]: {
    color: theme.palette.common.black,
  },
  [`& .${tooltipClasses.tooltip}`]: {
    backgroundColor: theme.palette.common.black,
    maxWidth: 220,
            color: "var(--dmg-bg)",
  },
}));

const FilterBar = ({
  sections,
  isSubmitted,
  formatFilterData,
  handleRemoveFilter,
}: FilterBarProps) => {
  const { values } = useFormikContext();

  const getLabelFromName = (name: string) => {
    const field =
      sections &&
      sections
        .flatMap((section) => section.fields)
        .find((field) => field.name === name);
    return field?.label || "N/A";
  };

  const getTooltipValue = (values: FormikValues, fieldName: string): string => {
    const originalValue = values[fieldName];

    if (Array.isArray(originalValue)) {
      return originalValue.map((item) => item.label).join(", ");
    } else if (
      originalValue &&
      typeof originalValue === "object" &&
      originalValue.label
    ) {
      return originalValue.label;
    } else if (typeof originalValue === "string") {
      return originalValue;
    }

    return "";
  };

  const filterItems = Object.entries(
    formatFilterData(values as FormikValues) as Query
  ).map(([fieldName]) => {
    const tooltipValue = getTooltipValue(values as FormikValues, fieldName);

    return (
      <FilterTooltip
        key={fieldName}
        arrow
        title={
          <Typography sx={classes.tooltip}>
            Search for value:
            <br />
            {tooltipValue}
          </Typography>
        }
      >
        <Chip
          label={`${getLabelFromName(fieldName)}: ${tooltipValue}`}
          onDelete={() => handleRemoveFilter(fieldName)}
          size="small"
          color="primary"
          sx={classes.chip}
          style={{
            color: "var(--dmg-bg)",
            textDecoration: "none",
            fontWeight: "bold",
          }}
        />
      </FilterTooltip>
    );
  });

  return (
    <Box>
      {values && !isEmpty(values) && isSubmitted ? (
        <Box sx={classes.filterWrapper}>
          <Box>
            <Tabs
              variant="scrollable"
              scrollButtons="auto"
              value={false}
              TabIndicatorProps={{ sx: classes.tabIndicator }}
              sx={classes.tabs}
            >
              <Box>{filterItems}</Box>
            </Tabs>
          </Box>
        </Box>
      ) : null}
    </Box>
  );
};

export default FilterBar;
